import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useMe } from '@/features/me/MeContext';
import { cn } from '@/lib/cn';

const ITEMS = [
  { id: 'profile', labelKey: 'profile.nav.profile', to: '/profile', end: true },
  { id: 'consents', labelKey: 'profile.nav.consents', to: '/profile/consents', end: true },
  { id: 'preferences', labelKey: 'profile.nav.preferences', to: '/profile/preferences', end: true },
  { id: 'subscription', labelKey: 'profile.nav.subscription', to: '/subscription', end: false },
] as const;

/**
 * The left submenu for `/profile` and `/subscription` (§ Profiel): a static
 * list, not edition-driven like `SubNav` — every caller sees the same items
 * regardless of edition or company. `end: false` on Abonnementen keeps it
 * active across its own subroutes (`/subscription/checkout`, `/return`,
 * `/withdraw`).
 */
export function ProfileNav({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const pendingCount = (me.pending_consents ?? []).length;

  return (
    <nav aria-label={t('profile.nav.label')}>
      <ul className="flex list-none flex-col gap-1 p-0">
        {ITEMS.map((item) => (
          <li key={item.id}>
            <NavLink
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'rounded-pill flex h-9 items-center justify-between gap-2 px-3 text-sm',
                  'hover:bg-mint-tint transition-colors motion-reduce:transition-none',
                  'focus-visible:ring-accent focus-visible:ring-offset-paper focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                  isActive ? 'bg-mint text-ink font-semibold' : 'text-ink',
                )
              }
            >
              {t(item.labelKey)}
              {item.id === 'consents' && pendingCount > 0 && (
                <span className="bg-danger flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] leading-none font-semibold text-white">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
